/**
 * FreshnessChip — data-freshness state indicator for ViewStore-backed widgets.
 *
 * Use when: a widget reads from ViewStore or any cached/streamed data source
 * where staleness matters. Tells the operator whether the panel is live,
 * stale, or paused — eliminating "silent stale data" bugs.
 *
 * Pattern from Datadog, Grafana, Bloomberg Terminal — "stale data is worse
 * than no data". Drive `state` from ViewStore TTL + `updated_at`:
 *   - live   → updated_at fresh (< TTL)
 *   - stale  → updated_at exceeds TTL but worker is still polling
 *   - paused → polling stopped (sync_worker disabled or auth gone)
 *   - error  → last fetch failed
 */
import { Caption1, Tooltip, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import {
  CheckmarkCircleFilled,
  ClockRegular,
  PauseCircleFilled,
  ErrorCircleFilled,
} from '@fluentui/react-icons';
import { useLocale } from '../i18n/useLocale';

export type FreshnessState = 'live' | 'stale' | 'paused' | 'error';

const STATE_META: Record<FreshnessState, { labelKey: string; tooltipKey: string; tokenKey: 'live' | 'stale' | 'paused' | 'error' }> = {
  live:   { labelKey: 'widget.freshness.live.label',   tooltipKey: 'widget.freshness.live.tooltip',   tokenKey: 'live' },
  stale:  { labelKey: 'widget.freshness.stale.label',  tooltipKey: 'widget.freshness.stale.tooltip',  tokenKey: 'stale' },
  paused: { labelKey: 'widget.freshness.paused.label', tooltipKey: 'widget.freshness.paused.tooltip', tokenKey: 'paused' },
  error:  { labelKey: 'widget.freshness.error.label',  tooltipKey: 'widget.freshness.error.tooltip',  tokenKey: 'error' },
};

const useStyles = makeStyles({
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusCircular,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  live:   { backgroundColor: tokens.colorPaletteGreenBackground1, color: tokens.colorPaletteGreenForeground1 },
  stale:  { backgroundColor: tokens.colorPaletteYellowBackground1, color: tokens.colorPaletteYellowForeground1 },
  paused: { backgroundColor: tokens.colorNeutralBackground3, color: tokens.colorNeutralForeground2 },
  error:  { backgroundColor: tokens.colorPaletteRedBackground1, color: tokens.colorPaletteRedForeground1 },
  /* Live state pulses subtly to indicate liveness without being distracting */
  pulse: {
    '@media (prefers-reduced-motion: no-preference)': {
      animationName: {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.7 },
      },
      animationDuration: '2s',
      animationIterationCount: 'infinite',
    },
  },
  age: {
    color: 'inherit',
    opacity: 0.8,
    fontVariantNumeric: 'tabular-nums',
  },
});

interface FreshnessChipProps {
  state: FreshnessState;
  /** ISO timestamp of last successful update. */
  updatedAt?: Date | string;
  /** Show "X ago" alongside the state. */
  showAge?: boolean;
}

type Translate = (key: string, params?: Record<string, string | number>) => string;

function formatAge(updated: Date, t: Translate): string {
  const sec = Math.max(0, Math.floor((Date.now() - updated.getTime()) / 1000));
  if (sec < 60) return t('common.time.secondsAgo', { n: sec });
  if (sec < 3600) return t('common.time.minutesAgo', { n: Math.floor(sec / 60) });
  if (sec < 86400) return t('common.time.hoursAgo', { n: Math.floor(sec / 3600) });
  return t('common.time.daysAgo', { n: Math.floor(sec / 86400) });
}

const ICON_MAP: Record<FreshnessState, React.ReactNode> = {
  live:   <CheckmarkCircleFilled />,
  stale:  <ClockRegular />,
  paused: <PauseCircleFilled />,
  error:  <ErrorCircleFilled />,
};

export function FreshnessChip({ state, updatedAt, showAge }: FreshnessChipProps) {
  const styles = useStyles();
  const { t } = useLocale();
  const meta = STATE_META[state];
  const label = t(meta.labelKey);
  const tooltip = t(meta.tooltipKey);
  const updated = updatedAt ? (typeof updatedAt === 'string' ? new Date(updatedAt) : updatedAt) : null;
  const ageLabel = updated && showAge ? formatAge(updated, t) : null;
  const tooltipText = updated
    ? t('widget.freshness.tooltipUpdated', { tooltip, updated: updated.toLocaleString() })
    : tooltip;

  return (
    <Tooltip content={tooltipText} relationship="description">
      <span
        className={mergeClasses(styles.chip, styles[meta.tokenKey], state === 'live' && styles.pulse)}
        role="status"
        aria-label={`${label}${ageLabel ? `, ${ageLabel}` : ''}`}
      >
        {ICON_MAP[state]}
        {label}
        {ageLabel ? <Caption1 className={styles.age}>· {ageLabel}</Caption1> : null}
      </span>
    </Tooltip>
  );
}
